import React, { useState, useRef, useEffect } from "react";

/**
 * Reusable draggable divider for splitting two components.
 *
 * Props:
 * - left: ReactNode — Left/top content
 * - right: ReactNode — Right/bottom content
 * - orientation: 'horizontal' | 'vertical' — Default: 'horizontal'
 * - initialSize: number or string — Initial width (horizontal) or height (vertical) in px or % (default: 300)
 * - minSize: number or string — Minimum size for the first pane (default: 100) px or %
 * - minSizeLeft: number or string — Minimum size for the first pane (optional) px or %
 * - minSizeRight: number or string — Minimum size for the second pane (optional) px or %
 * - maxSize: number or string — Maximum size for the first pane (optional) px or %
 * - onDragStart: function — Callback when dragging starts
 * - onDragEnd: function — Callback when dragging ends
 * - onSizeChanged: function — Callback when size changes
 * - className: string — Optional className for container
 * - style: object — Optional style for container
 */
export default function ResizableDivider({
    left,
    right,
    orientation = "horizontal",
    initialSize = 300 || "300px",
    minSize = 100 || "100px",
    maxSize,
    minSizeLeft,
    minSizeRight,
    className = "",
    style = {},
    disabled = false,
    dividerStyle = {},
    onDragStart = () => {},
    onDragEnd = () => {},
    onSizeChanged = () => {},
}) {
    const containerRef = useRef(null);
    const isDragging = useRef(false);

    const [dividerPos, setDividerPos] = useState(initialSize);
    const [hovering, setHovering] = useState(false);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        if (dragging) {
            onDragStart();
        } else {
            onDragEnd();
        }
    }, [dragging]);

    useEffect(() => {
        const timeout = setTimeout(
            () => {
                onSizeChanged();
            },
            dragging ? 0 : 250,
        );
        return () => clearTimeout(timeout);
        // onSizeChanged();
    }, [dividerPos, dragging]);

    useEffect(() => {
        setTimeout(() => setDividerPos(initialSize), 100);
    }, [initialSize]);

    useEffect(() => {
        const handleResize = () => {
            setDividerPos(initialSize);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [initialSize]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            setDragging(true);

            if (orientation === "horizontal") {
                let newX = e.clientX - rect.left;

                const max = maxSize || rect.width - (minSizeRight || minSize);
                const min = minSizeLeft || minSize;

                if (newX < min) newX = min;
                if (newX > max) newX = max;

                setDividerPos(newX);
            } else {
                let newY = e.clientY - rect.top;

                const max = maxSize || rect.height - (minSizeRight || minSize);
                const min = minSizeLeft || minSize;

                if (newY < min) newY = min;
                if (newY > max) newY = max;

                setDividerPos(newY);
            }
        };

        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.style.cursor = "default";
            document.body.style.userSelect = "auto";
            setDragging(false);
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [orientation, minSize, maxSize, minSizeLeft, minSizeRight, setDragging]);

    const handleMouseDown = () => {
        if (disabled) return;
        isDragging.current = true;
        document.body.style.cursor = orientation === "horizontal" ? "col-resize" : "row-resize";
        document.body.style.userSelect = "none";
    };

    const isHorizontal = orientation === "horizontal";

    return (
        <div
            ref={containerRef}
            className={`resizable-divider-container ${className}`}
            style={{
                display: "flex",
                flexDirection: isHorizontal ? "row" : "column",
                height: "100%",
                width: "100%",
                overflow: "hidden",
                ...style,
            }}>
            {/* First pane */}
            <div
                style={{
                    [isHorizontal ? "width" : "height"]: dividerPos,
                    flexShrink: 0,
                    // overflow: "auto",
                    position: "relative",
                    minWidth: isHorizontal ? minSizeLeft : undefined,
                    minHeight: !isHorizontal ? minSizeLeft : undefined,
                    transition: dragging ? `none` : `${isHorizontal ? "width" : "height"} 0.25s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}>
                {left}
            </div>

            {/* Divider */}
            <div
                onMouseDown={handleMouseDown}
                onMouseOver={() => !disabled && setHovering(true)}
                onMouseLeave={() => !disabled && setHovering(false)}
                style={{
                    cursor: disabled ? "default" : isHorizontal ? "col-resize" : "row-resize",
                    [isHorizontal ? "width" : "height"]: "14px",
                    background: "#1c1e30",
                    flexShrink: 0,
                    position: "relative",
                    zIndex: 10,
                    ...dividerStyle,
                }}>
                {/* Center line */}
                <div
                    style={{
                        position: "absolute",
                        top: isHorizontal ? 0 : "50%",
                        left: isHorizontal ? "50%" : 0,
                        width: isHorizontal ? "1px" : "100%",
                        height: isHorizontal ? "100%" : "1px",
                        background: hovering ? "#ffffff" : "#efeaf280",
                        transform: isHorizontal ? "translateX(-50%)" : "translateY(-50%)",
                        transition: "background 0.1s",
                    }}
                />

                {/* Knob */}
                {disabled ? null : (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            setDividerPos(initialSize);
                        }}
                        title={dividerPos !== initialSize ? "Reset divider position" : ""}
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: isHorizontal ? "8px" : "40px",
                            height: isHorizontal ? "40px" : "8px",
                            borderRadius: "3px",
                            background: "#ffffff",
                            boxShadow: "0 0 2px rgba(0,0,0,0.8)",
                            transition: "background 0.1s",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "2px",
                            cursor: dividerPos !== initialSize ? "pointer" : isHorizontal ? "col-resize" : "row-resize",
                            flexDirection: isHorizontal ? "column" : "row",
                        }}>
                        <div
                            style={{
                                width: isHorizontal ? "2px" : "6px",
                                height: isHorizontal ? "6px" : "2px",
                                background: hovering ? "#2c8dfc" : "#131313",
                                borderRadius: "1px",
                                opacity: 0.7,
                            }}
                        />
                        <div
                            style={{
                                width: isHorizontal ? "2px" : "6px",
                                height: isHorizontal ? "6px" : "2px",
                                background: hovering ? "#2c8dfc" : "#131313",
                                borderRadius: "1px",
                                opacity: 0.7,
                            }}
                        />
                    </div>
                )}
            </div>

            {/* Second pane */}
            <div
                style={{
                    flex: 1,
                    overflow: "auto",
                    minWidth: isHorizontal ? minSizeRight : undefined,
                    minHeight: !isHorizontal ? minSizeRight : undefined,
                    transition: dragging ? `none` : `${isHorizontal ? "width" : "height"} 0.25s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}>
                {right}
            </div>
        </div>
    );
}
